import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { IconArrowRight, IconLoader2 } from '@tabler/icons-react'
import { ForumSeo } from '@/components/ForumSeo'
import { Hero220 } from '@/components/home/Hero220'
import { cn } from '@/lib/cn'
import { apiFetch } from '@/lib/api'
import { clientSiteOrigin } from '@/lib/siteOrigin'
import { websiteSearchJsonLd } from '~shared/jsonld-web'
import type { ForumCategorySummary } from '@/lib/types'
import { LANDING_META_DESCRIPTION, LANDING_META_TITLE } from '~shared/seo'

const LEGACY_HOME_QUERY_KEYS = ['sort', 'category', 'q', 'page'] as const

const linkMuted = 'text-[14px] font-semibold text-sky-700 no-underline hover:text-zinc-900'

export function HomePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [categories, setCategories] = useState<ForumCategorySummary[]>([])
  const [catLoading, setCatLoading] = useState(true)
  const [catError, setCatError] = useState<string | null>(null)

  useEffect(() => {
    const hasLegacy = LEGACY_HOME_QUERY_KEYS.some(k => searchParams.has(k))
    if (hasLegacy) {
      const qs = searchParams.toString()
      navigate({ pathname: '/discussions', search: qs ? `?${qs}` : '' }, { replace: true })
    }
  }, [searchParams, navigate])

  useEffect(() => {
    let alive = true
    setCatLoading(true)
    void apiFetch<{ categories: ForumCategorySummary[] }>(`/api/categories`)
      .then(payload => {
        if (!alive) return
        setCategories(payload.categories ?? [])
        setCatError(null)
      })
      .catch(() => {
        if (!alive) return
        setCatError('Boards failed to load. You can still open discussions from the header.')
      })
      .finally(() => {
        if (!alive) return
        setCatLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  const shownBoards = categories.slice(0, 12)

  return (
    <>
      <ForumSeo
        title={LANDING_META_TITLE}
        description={LANDING_META_DESCRIPTION}
        jsonLd={websiteSearchJsonLd(clientSiteOrigin())}
      />

      {/* No space-y: Helmet emits a body <script>; the next sibling would get space-y margin above the hero. */}
      <div className="flex flex-col">
        <div className="relative left-1/2 z-[1] -mt-px w-screen max-w-[100vw] shrink-0 -translate-x-1/2">
          <Hero220 />
        </div>

        <section
          aria-labelledby="boards-heading"
          className="mt-6 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm shadow-zinc-950/5 md:mt-8"
        >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-100 bg-zinc-50/90 px-4 py-3 md:px-5">
          <div>
            <h2 id="boards-heading" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
              Boards
            </h2>
            <p className="mt-0.5 max-w-[62ch] text-[13px] text-zinc-600">Pick a board to see threads in that category.</p>
          </div>
          <Link to="/discussions" className={`inline-flex items-center gap-1 ${linkMuted} text-[13px]`}>
            All threads
            <IconArrowRight className="size-3.5" aria-hidden />
          </Link>
        </div>

        {catError ? (
          <p className="border-b border-amber-200 bg-amber-50 px-5 py-3 text-[14px] font-medium text-amber-950 md:px-6">{catError}</p>
        ) : null}

        <div className="p-4 md:p-5">
          {catLoading ? (
            <div className="flex items-center gap-2 px-1 py-8 text-[14px] font-medium text-zinc-600">
              <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden />
              Loading boards…
            </div>
          ) : shownBoards.length === 0 ? (
            <p className="px-1 py-6 text-[15px] text-zinc-600">No boards yet. Open discussions from the header to see what is live.</p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" role="list">
              {shownBoards.map(c => (
                <li key={c.id}>
                  <Link
                    to={`/c/${c.slug}`}
                    className="flex min-h-[4.5rem] flex-col justify-between gap-1 rounded-xl border border-zinc-200 bg-white px-4 py-3 no-underline shadow-sm transition hover:border-zinc-300 hover:bg-zinc-50"
                  >
                    <span className="font-semibold text-zinc-900">{c.name}</span>
                    {c.description?.trim() ? (
                      <span className="line-clamp-2 text-[13px] leading-snug text-zinc-600">{c.description.trim()}</span>
                    ) : (
                      <span className="text-[12px] font-medium tabular-nums text-zinc-500">{c.threadCount ?? 0} threads</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        </section>
      </div>
    </>
  )
}
